import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { uniqueId } from 'lodash';
import { ChangeEvent, ComponentPropsWithoutRef, forwardRef, ReactNode, useMemo } from 'react';
import styled, { css } from 'styled-components';

import useFormField from 'design-system/components/FormControl/useFormField';
import {
  isChecked,
  isDisabled,
  isFocused,
  isHovered,
  isNotDisabled,
} from 'design-system/lib/pseudoClass';
import { focusMixin, hoverFocusMixin, hoverMixin } from 'design-system/mixins/hoverable';
import { TransitionDuration } from 'design-system/variables/transitions';

const Container = styled.div`
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  position: relative;
  &${isDisabled} {
    cursor: default;
  }
`;

const Label = styled.label<{ disabled: boolean }>`
  cursor: inherit;
  padding: 0 ${Spacing[2]};
  color: ${Color.TextSecondary};
  ${Typography.TextMedium};

  ${({ theme, disabled }) =>
    disabled &&
    css`
      cursor: default;
      color: ${theme.color.text.disabled};
    `}
`;

const RadioCircle = styled.div`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: box-shadow ${TransitionDuration.Fast};
  border-radius: 100%;
  height: ${Spacing[4]};
  width: ${Spacing[4]};
  flex: 0 0 16px;
  box-sizing: border-box;
  margin-top: 2px;
  border: 1px solid ${Color.BorderInputCheckbox};
  background: ${Color.PrimaryWhite};
`;

const RadioInput = styled.input<{ checked?: boolean }>`
  cursor: inherit;
  height: ${Spacing[4]};
  width: ${Spacing[4]};
  margin: 0;
  border: 0;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  border-radius: 100%;

  &${isHovered}${isNotDisabled} ~ ${RadioCircle} {
    ${hoverMixin};
  }

  &${isFocused}${isNotDisabled} ~ ${RadioCircle} {
    ${focusMixin('primary')};
  }

  &${isHovered}${isFocused}${isNotDisabled} ~ ${RadioCircle} {
    ${hoverFocusMixin('primary')};
  }

  // RadioCircle state styles; Uses pure CSS
  &${isDisabled} + ${RadioCircle} {
    cursor: default;
    background: ${Color.BackgroundInputDisabled};
    border-color: ${Color.BorderInputDisabled};
  }

  &:checked + ${RadioCircle}, &${isChecked} + ${RadioCircle} {
    border-color: ${Color.BackgroundInputCheckboxChecked};
    background: ${Color.BackgroundInputCheckboxChecked};

    &::after {
      background: ${Color.BackgroundInputCheckbox};
      height: 6px;
      width: 6px;
      border-radius: 100%;
      content: '';
      position: absolute;
    }
  }

  &:checked${isDisabled} + ${RadioCircle}, &${isChecked}${isDisabled} + ${RadioCircle} {
    border-color: ${Color.BackgroundInputCheckboxDisabled};
    background: ${Color.BackgroundInputCheckboxDisabled};
  }
`;

export interface RadioProps extends ComponentPropsWithoutRef<'input'> {
  /**
   * If not provided, a unique ID will be generated.
   */
  id?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  label?: ReactNode;
  disabled?: boolean;
  checked?: boolean;
  value: string;
  'data-test'?: string;
}

const Radio = forwardRef<HTMLInputElement, RadioProps>(function Radio(
  {
    id,
    label,
    className,
    value,
    disabled,
    checked,
    defaultChecked,
    onChange,
    'data-test': dataTest,
    ...elemProps
  },
  ref,
) {
  const field = useFormField({ disabled });
  const generatedId = useMemo(() => uniqueId('pds_radio__'), []);

  return (
    <Container className={className} disabled={disabled} data-test={dataTest}>
      <RadioInput
        id={id || generatedId}
        disabled={field.disabled}
        onChange={onChange}
        type='radio'
        value={value}
        checked={checked}
        defaultChecked={defaultChecked}
        ref={ref}
        {...elemProps}
      />
      <RadioCircle />
      {label && (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <Label htmlFor={id || generatedId} disabled={disabled}>
          {label}
        </Label>
      )}
    </Container>
  );
});

export default Radio;
